export const PAYMENT_STATUS = Object.freeze({
  CREATED: {
    title: 'Created',
    color: '#6b7280'
  },
  PROCESSING: {
    title: 'Processing',
    color: '#6b7280'
  },
  CONFIRMED: {
    title: 'Confirmed',
    color: '#6b7280'
  },
  ACCEPTED: {
    title: 'Awaiting Shift Approval',
    color: '#f59e0b'
  },
  PENDING: {
    title: 'Sending Money',
    color: '#3b82f6'
  },
  COMPLETED: {
    title: 'Paid',
    color: '#10b981'
  },
  CANCELLED: {
    title: 'Rejected',
    color: '#ef4444'
  },
  SHIFTS_APPROVED: {
    title: 'Shifts Approved',
    color: '#3b82f6'
  }
})

export const STRIPE_PAYMENT_STATUS = Object.freeze({
  prepared: {
    label: 'Prepared',
    tone: 'neutral',
    description: 'Stripe prepared the payment operation. Processing has not started.'
  },
  processing: {
    label: 'Processing',
    tone: 'info',
    description: 'The ACH payment is processing. Do not submit another payment.'
  },
  succeeded: {
    label: 'Paid · funds clearing',
    tone: 'warning',
    description: 'The incoming payment was confirmed. Related payouts may remain on hold until funds clear.'
  },
  retry_scheduled: {
    label: 'Retry scheduled',
    tone: 'warning',
    description: 'Backend scheduled another collection attempt. Related payouts remain on hold.'
  },
  action_required: {
    label: 'Action required',
    tone: 'error',
    description: 'The bank account or payment authorization needs attention. Automatic processing is paused.'
  },
  failed: {
    label: 'Payment failed',
    tone: 'error',
    description: 'The Stripe payment operation failed. Review the payment before creating another one.'
  },
  manual_review: {
    label: 'Under review',
    tone: 'warning',
    description: 'This payment needs operator review. Do not create a duplicate payment.'
  },
  funding_processing: {
    label: 'Processing',
    tone: 'info',
    description: 'The incoming ACH funding is processing. Do not submit another payment.'
  },
  funding_succeeded: {
    label: 'Funds clearing',
    tone: 'warning',
    description: 'Funding was confirmed and is waiting for clearing or payout readiness.'
  },
  transfer_in_flight: {
    label: 'Transfer processing',
    tone: 'info',
    description: 'The transfer to the recipient is being created.'
  },
  transferred: {
    label: 'Transferred',
    tone: 'info',
    description: 'The transfer was confirmed. The payout may still be processing.'
  },
  payout_in_flight: {
    label: 'Payout processing',
    tone: 'info',
    description: "The payout was sent to the recipient's payout method and is still processing."
  },
  instant_fallback_pending: {
    label: 'Payment delayed',
    tone: 'warning',
    description: 'The instant payout did not complete. Stripe is preparing the standard payout fallback.'
  },
  paid: {
    label: 'Paid',
    tone: 'success',
    description: 'The payout was confirmed.'
  }
})

export const STRIPE_PROJECT_BALANCE_DEPOSIT_STATUS = Object.freeze({
  prepared: {
    label: 'Processing',
    tone: 'info',
    description: 'The bank transfer is being prepared. Funds are not available yet.'
  },
  funding_processing: {
    label: 'Processing',
    tone: 'info',
    description: 'Funds are not available until the bank transfer clears.'
  },
  funds_clearing: {
    label: 'Clearing',
    tone: 'info',
    description: 'The bank transfer is clearing. Funds are not available yet.'
  },
  transfer_in_flight: {
    label: 'Transferring',
    tone: 'info',
    description: 'Cleared funds are being added to your available balance.'
  },
  completed: {
    label: 'Available',
    tone: 'success',
    description: 'The bank transfer has cleared.'
  },
  action_required: {
    label: 'Action required',
    tone: 'warning',
    description: 'Update your bank account before this top-up can continue.'
  },
  failed: {
    label: 'Failed',
    tone: 'error',
    description: 'Funds were not added. Your available balance has not changed.'
  },
  manual_review: {
    label: 'Review required',
    tone: 'warning',
    description: 'This top-up needs review. Do not submit another top-up.'
  }
})

export const PAYMENT_METHOD = Object.freeze({
  hourly: 'Hourly',
  salary: 'Salary',
  marketplace_rate: 'Marketplace Rate'
})

export const PAYMENT_WORK_STATUS = Object.freeze({
  full_time: 'Full Time',
  part_time: 'Part Time',
  on_call: 'On Call'
})

export const PAYMENT_TYPE = Object.freeze({
  employee: 'Employee',
  contractor: 'Contractor',
  volunteer: 'Volunteer'
})

export const PAYMENT_SYSTEM_TEMPLATE_ID = Object.freeze({
  deel: 1,
  legacyStripe: 2,
  wise: 3,
  manual: 4
})

export const RETIRED_PAYMENT_SYSTEM_TEMPLATE_IDS = Object.freeze([
  PAYMENT_SYSTEM_TEMPLATE_ID.deel,
  PAYMENT_SYSTEM_TEMPLATE_ID.wise
])

export const isStripeV2PaymentSystem = paymentSystem =>
  paymentSystem?.template_id === null && paymentSystem?.is_global === true && paymentSystem?.name === 'Stripe V2'

export const isLegacyStripePaymentSystem = paymentSystem =>
  paymentSystem?.template_id === PAYMENT_SYSTEM_TEMPLATE_ID.legacyStripe && !paymentSystem?.is_global

export const isCurrentPaymentSystem = paymentSystem => {
  const templateId = Object.prototype.hasOwnProperty.call(paymentSystem, 'template_id')
    ? paymentSystem.template_id
    : paymentSystem.id

  return templateId === null || !RETIRED_PAYMENT_SYSTEM_TEMPLATE_IDS.includes(templateId)
}

export const REQUEST_TYPE = Object.freeze({
  advance: 'Advance',
  payroll: 'Payroll',
  payment: 'Payment',
  invoice: 'Invoice',
  company_invoice: 'Company Invoice',
  manual_payment: 'Manual Payment'
})
